import { useCallback, useMemo } from "react";
import { useSelectableLegalEntityTypesQuery } from "@common/hooks/useSelectableItemQueries.ts";
import { StandardLegalEntityType } from "./LegalEntity.types.ts";

// The types are master data the customer can add to, so anything showing one looks its name up instead of
// translating an enum member. Table cells resolve one name per row, so the lookups are built once per fetch
// and the getters keep a stable identity.
export function useLegalEntityTypeNames() {
    const { data, isLoading } = useSelectableLegalEntityTypesQuery();

    const namesById = useMemo(() => new Map((data ?? []).map((legalEntityType) => [legalEntityType.id, legalEntityType.name])), [data]);

    const standardTypesById = useMemo(() => new Map((data ?? []).map((legalEntityType) => [legalEntityType.id, legalEntityType.standardType])), [data]);

    const idsByStandardType = useMemo(() => new Map((data ?? []).map((legalEntityType) => [legalEntityType.standardType, legalEntityType.id])), [data]);

    const getLegalEntityTypeName = useCallback((typeId: string) => namesById.get(typeId) ?? "", [namesById]);

    const getStandardType = useCallback((typeId: string) => standardTypesById.get(typeId), [standardTypesById]);

    // Undefined until the types have loaded, so a caller that seeds a type has to resolve it when it is used,
    // not on first render.
    const getStandardTypeId = useCallback((standardType: StandardLegalEntityType) => idsByStandardType.get(standardType), [idsByStandardType]);

    return {
        legalEntityTypes: data ?? [],
        getLegalEntityTypeName,
        getStandardType,
        getStandardTypeId,
        isLoadingLegalEntityTypes: isLoading,
    };
}
